import { useCallback, useEffect, useRef, useState } from "react";
import {
  Map as MapLibreKarte,
  Marker,
  NavigationControl,
  Popup,
  ScaleControl,
  type GeoJSONSource,
  type LngLatBoundsLike,
  type StyleSpecification,
} from "maplibre-gl";
import {
  Bus,
  Car,
  ChevronDown,
  ChevronRight,
  Glasses,
  MapPin,
  Smartphone,
  Tag,
  User,
  Battery,
  Fuel,
  Gauge,
  Navigation,
  Zap,
  RefreshCw,
} from "lucide-react";

import {
  api,
  type Erdbeben,
  type Flug,
  type FlugDetail,
  type GeraetMitStatus,
  type Ladestation,
  type LadepreisAntwort,
  type PersonenUebersicht,
  type PersonMitGeraeten,
  type PositionEintrag,
  type QuelleInfo,
  type Schiff,
} from "@/api.ts";
import { zeitpunktLokal } from "@/zeit.ts";
import { ortungAnzeige, ortungAus } from "@/ortung.ts";
import { googleMapsUrl } from "@/karten.ts";
import { OverlayAuswahl, overlaysLaden } from "@/komponenten/OverlayAuswahl.tsx";
import { radarBilderLaden, type RadarBild } from "@/overlays/regenradar.ts";

const GERAET_SYMBOL: Record<string, typeof Car> = {
  car: Car,
  bus: Bus,
  smartphone: Smartphone,
  glasses: Glasses,
  tag: Tag,
  findmy: MapPin,
  user: User,
};

/**
 * Deutsche Beschriftung für die Telemetrie, die Encom aus den HA-Sensoren
 * übernimmt. Was hier fehlt, wird aus dem Schlüssel gebildet — ein neues
 * Fahrzeug zeigt seine Werte also auch ohne Eintrag, nur mit rohem Namen.
 */
const TELEMETRIE_ETIKETT: Record<string, string> = {
  battery: "Spannung",
  temperature: "Temperatur",
  ladezustand: "Ladezustand",
  reichweite: "Reichweite",
  kilometerzahler: "Kilometerstand",
  innenraumtemperatur: "Innen",
  aussentemperatur: "Außen",
  battery_power: "Leistung",
};

/** Schlüssel, die schon als eigene Anzeige laufen oder keine Messwerte sind. */
const TELEMETRIE_GEZEIGT = new Set([
  "batterie",
  "battery_hv_state_of_charge",
  "range_total_range_last_sent",
  "range_tank_level",
  "vehicle_mileage",
  "iphone_battery_level",
]);

interface Messwert {
  schluessel: string;
  etikett: string;
  text: string;
}

/**
 * Die übrige Telemetrie, die keine eigene Zeile hat.
 *
 * Nur Zahlen: `connected: "on"` und Geschwister sind Zustände, keine Messwerte,
 * und blähten die Karte auf, ohne etwas zu sagen.
 */
function weitereMesswerte(meta: Record<string, unknown>, grenze = 4): Messwert[] {
  const werte: Messwert[] = [];

  for (const [schluessel, roh] of Object.entries(meta)) {
    if (TELEMETRIE_GEZEIGT.has(schluessel) || werte.length >= grenze) continue;

    let zahl: number;
    let einheit = "";
    if (typeof roh === "object" && roh !== null && "wert" in roh) {
      const obj = roh as { wert: number; einheit?: string };
      if (typeof obj.wert !== "number") continue;
      zahl = obj.wert;
      einheit = obj.einheit ?? "";
    } else if (typeof roh === "number") {
      zahl = roh;
    } else {
      continue;
    }

    const etikett =
      TELEMETRIE_ETIKETT[schluessel] ??
      schluessel.replace(/_/g, " ").replace(/^./, (z) => z.toUpperCase());

    werte.push({
      schluessel,
      etikett,
      text: `${zahl.toLocaleString("de-DE")}${einheit ? ` ${einheit}` : ""}`,
    });
  }

  return werte;
}

export function LiveSeite() {
  const [quellen, setQuellen] = useState<QuelleInfo[]>([]);
  const [positionen, setPositionen] = useState<PositionEintrag[]>([]);
  const [uebersicht, setUebersicht] = useState<PersonenUebersicht | null>(null);
  const [aktiv, setAktiv] = useState<string | null>(null);
  const [lade, setLade] = useState(true);

  const laden = useCallback(async () => {
    try {
      const [q, p, u] = await Promise.all([
        api.quellen(),
        api.positionenAktuell(),
        api.personenUebersicht(),
      ]);
      setQuellen(q);
      setPositionen(p);
      setUebersicht(u);
    } catch {
      // Fehler wird ignoriert — nächster Versuch in 30 Sek
    } finally {
      setLade(false);
    }
  }, []);

  useEffect(() => {
    void laden();
    const timer = setInterval(() => void laden(), 30_000);
    return () => clearInterval(timer);
  }, [laden]);

  if (lade && positionen.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <RefreshCw className="animate-spin text-text-muted" size={24} />
      </div>
    );
  }

  if (quellen.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <div>
          <h2 className="text-xl font-semibold">Keine Quellen konfiguriert</h2>
          <p className="mt-2 text-text-muted">
            Richte in der Datenbank mindestens eine Positionsquelle ein.
          </p>
        </div>
      </div>
    );
  }

  const hatPersonen = uebersicht && uebersicht.personen.length > 0;

  return (
    <div className="flex h-full flex-col lg:flex-row">
      <div className="order-2 flex flex-col gap-3 overflow-y-auto p-4 lg:order-1 lg:w-96">
        {hatPersonen ? (
          <PersonenSidebar
            uebersicht={uebersicht!}
            positionen={positionen}
            quellen={quellen}
            aktiv={aktiv}
            beiWahl={setAktiv}
          />
        ) : (
          quellen.map((q) => {
            const pos = positionen.find((p) => p.quelleId === q.id);
            return (
              <GeraeteKarte
                key={q.id}
                quelle={q}
                position={pos ?? null}
                ausgewaehlt={aktiv === q.id}
                beiKlick={() => setAktiv(aktiv === q.id ? null : q.id)}
              />
            );
          })
        )}
      </div>
      <div className="order-1 min-h-[300px] flex-1 lg:order-2">
        <LiveKarte positionen={positionen} aktiv={aktiv} />
      </div>
    </div>
  );
}

function PersonenSidebar({
  uebersicht,
  positionen,
  quellen,
  aktiv,
  beiWahl,
}: {
  uebersicht: PersonenUebersicht;
  positionen: PositionEintrag[];
  quellen: QuelleInfo[];
  aktiv: string | null;
  beiWahl: (id: string | null) => void;
}) {
  return (
    <>
      {uebersicht.personen.map((person) => (
        <PersonenGruppe
          key={person.id}
          person={person}
          positionen={positionen}
          quellen={quellen}
          aktiv={aktiv}
          beiWahl={beiWahl}
        />
      ))}
      {uebersicht.ohnePerson.length > 0 && (
        <div className="mt-2">
          <div className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
            Sonstige
          </div>
          {uebersicht.ohnePerson.map((g) => {
            const q = quellen.find((qu) => qu.id === g.id);
            const pos = positionen.find((p) => p.quelleId === g.id);
            if (!q) return null;
            return (
              <GeraeteKarte
                key={g.id}
                quelle={q}
                position={pos ?? null}
                ausgewaehlt={aktiv === g.id}
                beiKlick={() => beiWahl(aktiv === g.id ? null : g.id)}
                kompakt
                status={g.status}
              />
            );
          })}
        </div>
      )}
    </>
  );
}

function PersonenGruppe({
  person,
  positionen,
  quellen,
  aktiv,
  beiWahl,
}: {
  person: PersonMitGeraeten;
  positionen: PositionEintrag[];
  quellen: QuelleInfo[];
  aktiv: string | null;
  beiWahl: (id: string | null) => void;
}) {
  const bewegt = person.geraete.filter((g) => g.status === "bewegt");
  const rest = person.geraete.filter((g) => g.status !== "bewegt");
  const [restOffen, setRestOffen] = useState(false);

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 px-1">
        <span
          className="size-3 rounded-full"
          style={{ backgroundColor: person.farbe }}
        />
        <span className="text-sm font-semibold text-text">{person.name}</span>
        {bewegt.length > 0 && (
          <span className="rounded-full bg-success/10 px-1.5 text-xs font-medium text-success">
            {bewegt.length} bewegt
          </span>
        )}
      </div>

      {bewegt.map((g) => {
        const q = quellen.find((qu) => qu.id === g.id);
        const pos = positionen.find((p) => p.quelleId === g.id);
        if (!q) return null;
        return (
          <GeraeteKarte
            key={g.id}
            quelle={q}
            position={pos ?? null}
            ausgewaehlt={aktiv === g.id}
            beiKlick={() => beiWahl(aktiv === g.id ? null : g.id)}
            status={g.status}
          />
        );
      })}

      {rest.length > 0 && (
        <>
          <button
            onClick={() => setRestOffen(!restOffen)}
            className="flex w-full items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-page-bg"
          >
            {restOffen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            {rest.length} ruhend{rest.length > 1 ? "e" : ""}{rest.filter((g) => g.status === "veraltet").length > 0 && " / veraltet"}
          </button>
          {restOffen &&
            rest.map((g) => {
              const q = quellen.find((qu) => qu.id === g.id);
              const pos = positionen.find((p) => p.quelleId === g.id);
              if (!q) return null;
              return (
                <GeraeteKarte
                  key={g.id}
                  quelle={q}
                  position={pos ?? null}
                  ausgewaehlt={aktiv === g.id}
                  beiKlick={() => beiWahl(aktiv === g.id ? null : g.id)}
                  kompakt
                  status={g.status}
                />
              );
            })}
        </>
      )}

      {person.geraete.length === 0 && (
        <p className="px-2 py-1 text-xs text-text-muted">Keine Geräte</p>
      )}
    </div>
  );
}

function GeraeteKarte({
  quelle,
  position,
  ausgewaehlt,
  beiKlick,
  kompakt,
  status,
}: {
  quelle: QuelleInfo;
  position: PositionEintrag | null;
  ausgewaehlt: boolean;
  beiKlick: () => void;
  kompakt?: boolean;
  status?: GeraetMitStatus["status"];
}) {
  const Symbol = GERAET_SYMBOL[quelle.symbol ?? ""] ?? Navigation;
  const meta = position?.meta ?? {};
  const veraltet = status === "veraltet";

  if (kompakt) {
    return (
      <button
        onClick={beiKlick}
        className={`flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left transition-all ${
          ausgewaehlt
            ? "bg-blue-50 shadow-sm"
            : "hover:bg-page-bg"
        } ${veraltet ? "opacity-50" : ""}`}
      >
        <div
          className="flex size-6 items-center justify-center rounded-full text-white"
          style={{ backgroundColor: quelle.farbe }}
        >
          <Symbol size={13} />
        </div>
        <span className="min-w-0 flex-1 truncate text-sm text-text">{quelle.name}</span>
        <span className="text-xs text-text-muted">
          {position ? zeitpunktLokal(position.zeitpunkt) : "–"}
        </span>
      </button>
    );
  }

  return (
    <button
      onClick={beiKlick}
      className={`w-full rounded-xl border p-4 text-left transition-all ${
        ausgewaehlt
          ? "border-primary bg-blue-50 shadow-md"
          : "border-border bg-surface hover:shadow"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex size-10 items-center justify-center rounded-full text-white"
          style={{ backgroundColor: quelle.farbe }}
        >
          <Symbol size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-text">{quelle.name}</div>
          <div className="text-xs text-text-muted">
            {position ? zeitpunktLokal(position.zeitpunkt) : "Keine Daten"}
          </div>
        </div>
        {status === "bewegt" && (
          <span className="shrink-0 rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
            bewegt
          </span>
        )}
        {!status && (
          <div className="text-right text-xs text-text-muted">
            {quelle.anzahl.toLocaleString("de-DE")} Punkte
          </div>
        )}
      </div>

      {position && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-muted">
          <MetaWert meta={meta} schluessel="batterie" symbol={Battery} einheit="%" />
          <MetaWert meta={meta} schluessel="battery_hv_state_of_charge" symbol={Zap} label="HV" />
          <MetaWert meta={meta} schluessel="range_total_range_last_sent" symbol={Gauge} label="Reichweite" />
          <MetaWert meta={meta} schluessel="range_tank_level" symbol={Fuel} label="Tank" />
          <MetaWert meta={meta} schluessel="vehicle_mileage" symbol={Navigation} label="km" />
          <MetaWert meta={meta} schluessel="iphone_battery_level" symbol={Battery} einheit="%" />
          {weitereMesswerte(meta).map((w) => (
            <span key={w.schluessel} title={w.schluessel}>
              {w.etikett}: {w.text}
            </span>
          ))}
          {(() => {
            const anz = ortungAnzeige(ortungAus(meta));
            if (!anz) return null;
            return (
              <span
                className="flex items-center gap-1 rounded px-1.5 font-medium"
                style={{ color: anz.farbe, backgroundColor: `${anz.farbe}1a` }}
                title={anz.erklaerung}
              >
                {anz.etikett}
                {position.genauigkeit != null && position.genauigkeit > 0 && (
                  <span className="font-normal opacity-70">±{lesbareEntfernung(position.genauigkeit)}</span>
                )}
              </span>
            );
          })()}
          {ortungAus(meta) === null && position.genauigkeit != null && position.genauigkeit > 0 && (
            <span title="Genauigkeit">±{lesbareEntfernung(position.genauigkeit)}</span>
          )}
          {position.hoehe != null && (
            <span title="Höhe">{Math.round(position.hoehe)} m ü.M.</span>
          )}
        </div>
      )}
    </button>
  );
}

/** Meter lesbar: „±20 m", „±2,4 km". */
function lesbareEntfernung(meter: number): string {
  if (meter >= 1000) return `${(meter / 1000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} km`;
  return `${Math.round(meter)} m`;
}

/** Ein Flugzeug-Symbol, in Flugrichtung gedreht. */
function flugSvg(kurs: number | null): string {
  const dreh = kurs != null ? `transform="rotate(${kurs} 12 12)"` : "";
  return `<svg width="24" height="24" viewBox="0 0 24 24" style="filter:drop-shadow(0 1px 1px rgba(0,0,0,.4))">
    <g ${dreh}>
      <path d="M12 2 L13.2 10 L21 14 L21 15.6 L13.2 13.4 L13 19 L15.5 20.6 L15.5 22 L12 21 L8.5 22 L8.5 20.6 L11 19 L10.8 13.4 L3 15.6 L3 14 L10.8 10 Z"
        fill="#1e293b" stroke="white" stroke-width="0.6"/>
    </g>
  </svg>`;
}

function flugPopup(f: Flug): string {
  const z: string[] = [`<b>${f.rufzeichen ?? f.id}</b>`];
  if (f.hoehe != null) z.push(`Höhe: ${Math.round(f.hoehe).toLocaleString("de-DE")} m`);
  if (f.geschwindigkeit != null) z.push(`Tempo: ${Math.round(f.geschwindigkeit * 3.6)} km/h`);
  if (f.steigen != null && Math.abs(f.steigen) > 0.5) z.push(f.steigen > 0 ? "↑ steigt" : "↓ sinkt");
  z.push('<span style="color:#64748b">klicken für Details</span>');
  return `<div style="font:12px/1.5 system-ui;color:#1e293b;white-space:nowrap">${z.join("<br>")}</div>`;
}

/** Das ausführliche Flug-Popup: Basisdaten sofort, Strecke/Maschine nachgeladen. */
function flugDetailHtml(f: Flug, detail: FlugDetail | null, fehlgeschlagen = false): string {
  const kopf = detail?.airline
    ? `<b>${f.rufzeichen ?? f.id}</b> · ${detail.airline}`
    : `<b>${f.rufzeichen ?? f.id}</b>`;
  const z: string[] = [kopf];

  const ort = (o: FlugDetail["von"]) =>
    o ? `${o.iata ? `<b>${o.iata}</b> ` : ""}${o.stadt ?? o.name ?? ""}` : "?";
  if (detail?.von || detail?.nach) {
    z.push(`${ort(detail.von)} → ${ort(detail.nach)}`);
  }
  if (detail?.flugzeug) z.push(`✈ ${detail.flugzeug}`);
  if (detail?.registrierung) z.push(`<span style="color:#64748b">${detail.registrierung}</span>`);

  const werte: string[] = [];
  if (f.hoehe != null) werte.push(`${Math.round(f.hoehe).toLocaleString("de-DE")} m`);
  if (f.geschwindigkeit != null) werte.push(`${Math.round(f.geschwindigkeit * 3.6)} km/h`);
  if (f.steigen != null && Math.abs(f.steigen) > 0.5) werte.push(f.steigen > 0 ? "↑" : "↓");
  if (f.amBoden) werte.push("am Boden");
  if (werte.length) z.push(`<span style="color:#64748b">${werte.join(" · ")}</span>`);

  if (!detail && !fehlgeschlagen) z.push('<span style="color:#94a3b8">lädt Details …</span>');
  if (fehlgeschlagen) z.push('<span style="color:#94a3b8">keine Streckendaten</span>');

  return `<div style="font:12px/1.6 system-ui;color:#1e293b;min-width:150px">${z.join("<br>")}</div>`;
}

/** Ein kleines Temperaturschild, farblich von kalt (blau) bis warm (rot). */
function tempFahne(temp: number): string {
  const t = Math.round(temp);
  const farbe = t <= 0 ? "#2563eb" : t < 10 ? "#0891b2" : t < 20 ? "#16a34a" : t < 28 ? "#d97706" : "#dc2626";
  return `<div style="font:600 11px/1 system-ui;color:white;background:${farbe};padding:2px 5px;border-radius:6px;box-shadow:0 1px 2px rgba(0,0,0,.3);white-space:nowrap">${t}°</div>`;
}

/** Windfähnchen: ein Pfeil in Windrichtung (wohin es weht) mit der km/h-Zahl. */
function windFahne(kmh: number, richtungAus: number | null): string {
  // Meteorologisch kommt der Wind AUS `richtungAus`; der Pfeil zeigt, WOHIN —
  // also die Gegenrichtung.
  const nach = richtungAus != null ? (richtungAus + 180) % 360 : 0;
  const pfeil =
    richtungAus != null
      ? `<svg width="16" height="16" viewBox="0 0 16 16" style="transform:rotate(${nach}deg)"><path d="M8 1 L8 15 M8 1 L4.5 5 M8 1 L11.5 5" stroke="white" stroke-width="2" fill="none" stroke-linecap="round"/></svg>`
      : "";
  return `<div style="display:flex;align-items:center;gap:2px;font:600 10px/1 system-ui;color:white;background:#0369a1;padding:2px 4px;border-radius:6px;box-shadow:0 1px 2px rgba(0,0,0,.3);white-space:nowrap">${pfeil}${Math.round(kmh)}</div>`;
}

/** Ein Schiffssymbol (Rumpf von oben), in Fahrtrichtung gedreht. */
function schiffSvg(kurs: number | null): string {
  const dreh = kurs != null ? `transform="rotate(${kurs} 12 12)"` : "";
  return `<svg width="20" height="20" viewBox="0 0 24 24" style="filter:drop-shadow(0 1px 1px rgba(0,0,0,.4))">
    <g ${dreh}>
      <path d="M12 2 L16 9 L16 20 L8 20 L8 9 Z" fill="#0f766e" stroke="white" stroke-width="1.2"/>
    </g>
  </svg>`;
}

/** Ein Ladesäulen-Symbol (grün, Blitz). */
function ladeSvg(): string {
  return `<svg width="26" height="26" viewBox="0 0 24 24" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.4))">
    <circle cx="12" cy="12" r="11" fill="#16a34a" stroke="white" stroke-width="2"/>
    <path d="M13 5 L8 13 L11.5 13 L11 19 L16 11 L12.5 11 Z" fill="white"/>
  </svg>`;
}

function ladeDetailHtml(s: Ladestation, preise: LadepreisAntwort | null, fehlgeschlagen = false): string {
  const z: string[] = [`<b>${s.name}</b>`];
  if (s.betreiber && s.betreiber !== s.name) z.push(s.betreiber);
  const technik: string[] = [];
  const kw = preise?.ladepunkt.kw ?? s.kw;
  if (kw) technik.push(`${kw} kW`);
  if (preise?.ladepunkt.stecker) technik.push(preise.ladepunkt.stecker);
  if (s.ladepunkte) technik.push(`${s.ladepunkte} Punkte`);
  if (technik.length) z.push(`<span style="color:#64748b">${technik.join(" · ")}</span>`);

  if (preise) {
    if (preise.tarife.length === 0) {
      z.push('<span style="color:#94a3b8">keine Preisdaten für diese Station</span>');
    } else {
      z.push('<span style="color:#64748b;font-size:11px">Günstigste Tarife (30 kWh):</span>');
      for (const t of preise.tarife.slice(0, 4)) {
        const proKwh = t.proKwh != null ? ` <span style="color:#94a3b8">(${t.proKwh.toLocaleString("de-DE")} €/kWh)</span>` : "";
        const abo = t.monatsgebuehr ? ' <span style="color:#d97706">+Abo</span>' : "";
        z.push(`<b>${t.preis.toLocaleString("de-DE")} ${t.waehrung}</b> ${t.anbieter ?? ""}${proKwh}${abo}`);
      }
      if (preise.tarife.length > 4) z.push(`<span style="color:#94a3b8">… und ${preise.tarife.length - 4} weitere</span>`);
    }
  } else if (fehlgeschlagen) {
    z.push('<span style="color:#94a3b8">Preise nicht verfügbar</span>');
  } else {
    z.push('<span style="color:#94a3b8">lädt Preise …</span>');
  }
  return `<div style="font:12px/1.6 system-ui;color:#1e293b;min-width:170px">${z.join("<br>")}</div>`;
}

/** Ein Satelliten-Symbol für die ISS. */
function issSvg(): string {
  return `<svg width="30" height="30" viewBox="0 0 32 32" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.5))">
    <rect x="13" y="12" width="6" height="8" rx="1" fill="#e2e8f0" stroke="#334155" stroke-width="1"/>
    <rect x="3" y="13" width="8" height="6" fill="#1d4ed8" stroke="#93c5fd" stroke-width="0.8"/>
    <rect x="21" y="13" width="8" height="6" fill="#1d4ed8" stroke="#93c5fd" stroke-width="0.8"/>
    <line x1="11" y1="16" x2="13" y2="16" stroke="#334155" stroke-width="1"/>
    <line x1="19" y1="16" x2="21" y2="16" stroke="#334155" stroke-width="1"/>
  </svg>`;
}

/** Ein Kreisring aus 64 Punkten um einen Ort, Radius in Metern. */
function kreisRing(lon: number, lat: number, radiusM: number, punkte = 64): [number, number][] {
  const dLat = radiusM / 111_320;
  const dLon = radiusM / (111_320 * Math.cos((lat * Math.PI) / 180));
  const ring: [number, number][] = [];
  for (let i = 0; i <= punkte; i++) {
    const w = (i / punkte) * 2 * Math.PI;
    ring.push([lon + dLon * Math.cos(w), lat + dLat * Math.sin(w)]);
  }
  return ring;
}

function MetaWert({
  meta,
  schluessel,
  symbol: Symbol,
  label,
  einheit,
}: {
  meta: Record<string, unknown>;
  schluessel: string;
  symbol: typeof Battery;
  label?: string;
  einheit?: string;
}) {
  const roh = meta[schluessel];
  if (roh === undefined || roh === null) return null;

  let wert: string;
  let unit = einheit ?? "";

  if (typeof roh === "object" && roh !== null && "wert" in roh) {
    const obj = roh as { wert: number; einheit?: string };
    wert = obj.wert.toLocaleString("de-DE");
    unit = obj.einheit ?? unit;
  } else if (typeof roh === "number") {
    wert = roh.toLocaleString("de-DE");
  } else {
    wert = String(roh);
  }

  return (
    <span className="flex items-center gap-1" title={label ?? schluessel}>
      <Symbol size={12} />
      {label ? `${label}: ` : ""}
      {wert} {unit}
    </span>
  );
}

// ------------------------------------------------------------------ Karte

function LiveKarte({
  positionen,
  aktiv,
}: {
  positionen: PositionEintrag[];
  aktiv: string | null;
}) {
  const behaelter = useRef<HTMLDivElement>(null);
  const karte = useRef<MapLibreKarte | null>(null);
  // Zustand, nicht Ref: Kommen die Positionen an, bevor die Karte fertig
  // geladen ist, brach der Marker-Effekt ab und lief nie nach — die Karte
  // blieb ohne Icons, und ein Klick zentrierte auf ein leeres Feld. Als
  // State löst das Laden ein erneutes Zeichnen aus.
  const [bereit, setBereit] = useState(false);
  const [overlays, setOverlays] = useState<Set<string>>(() => overlaysLaden());
  const markerMap = useRef<Map<string, Marker>>(new Map());
  const popup = useRef<Popup | null>(null);
  // Getrennt vom flüchtigen Hover-Popup: dieses bleibt beim Klick stehen, damit
  // der Google-Maps-Link darin erreichbar ist.
  const klickPopup = useRef<Popup | null>(null);
  // Marker werden über Aktualisierungen hinweg wiederverwendet; ihre
  // Klick-Handler dürfen deshalb nicht die Position von damals festhalten,
  // sondern lesen die aktuelle über diesen Ref.
  const positionenRef = useRef(positionen);
  positionenRef.current = positionen;
  const flugMarker = useRef<Map<string, Marker>>(new Map());
  const flugDatenRef = useRef<Flug[]>([]);
  const tempMarker = useRef<Map<string, Marker>>(new Map());
  const windMarker = useRef<Map<string, Marker>>(new Map());
  const issMarker = useRef<Marker | null>(null);
  const ladeMarker = useRef<Map<string, Marker>>(new Map());
  const ladeDatenRef = useRef<Ladestation[]>([]);
  const schiffMarker = useRef<Map<number, Marker>>(new Map());
  const schiffDatenRef = useRef<Schiff[]>([]);

  useEffect(() => {
    if (!behaelter.current || karte.current) return;

    const stil: StyleSpecification = {
      version: 8,
      sources: {
        osm: {
          type: "raster",
          tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
          tileSize: 256,
          maxzoom: 19,
          attribution:
            '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        },
      },
      layers: [{ id: "osm", type: "raster", source: "osm" }],
    };

    const k = new MapLibreKarte({
      container: behaelter.current,
      style: stil,
      center: [7.79, 50.79],
      zoom: 12,
      attributionControl: { compact: true },
      preserveDrawingBuffer: true,
    });

    k.addControl(new NavigationControl({ showCompass: false }), "top-right");
    k.addControl(new ScaleControl({ unit: "metric" }), "bottom-left");

    karte.current = k;
    k.once("load", () => {
      setBereit(true);
    });

    return () => {
      popup.current?.remove();
      klickPopup.current?.remove();
      k.remove();
      karte.current = null;
      setBereit(false);
    };
  }, []);

  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    for (const [id, m] of markerMap.current) {
      if (!positionen.find((p) => p.quelleId === id)) {
        m.remove();
        markerMap.current.delete(id);
      }
    }

    const grenzen: [number, number][] = [];

    for (const pos of positionen) {
      // Bei grober Ortung nicht auf den Punkt zoomen, sondern auf den
      // Unschärfebereich — sonst füllt der Kreis das Bild und ist als Kreis
      // nicht erkennbar.
      const ort = ortungAus(pos.meta);
      if (ort?.grob && pos.genauigkeit != null && pos.genauigkeit >= 100) {
        const dLat = pos.genauigkeit / 111_320;
        const dLon = pos.genauigkeit / (111_320 * Math.cos((pos.lat * Math.PI) / 180));
        grenzen.push([pos.lon - dLon, pos.lat - dLat], [pos.lon + dLon, pos.lat + dLat]);
      } else {
        grenzen.push([pos.lon, pos.lat]);
      }

      let marker = markerMap.current.get(pos.quelleId);
      if (marker) {
        marker.setLngLat([pos.lon, pos.lat]);
        // Das Symbol neu setzen, damit Richtung und Farbe aktuell bleiben —
        // die alte Zeile griff ins Positionierungs-Transform von MapLibre und
        // war wirkungslos.
        marker.getElement().innerHTML = geraeteSvg(pos.quelleArt, pos.farbe, pos.richtung);
        continue;
      }

      const el = document.createElement("div");
      el.innerHTML = geraeteSvg(pos.quelleArt, pos.farbe, pos.richtung);
      el.style.cursor = "pointer";

      el.addEventListener("mouseenter", () => {
        if (!popup.current) {
          popup.current = new Popup({ closeButton: false, closeOnClick: false, offset: 20 });
        }
        popup.current
          .setLngLat([pos.lon, pos.lat])
          .setHTML(popupHtml(pos))
          .addTo(k);
      });
      el.addEventListener("mouseleave", () => popup.current?.remove());

      // Klick öffnet ein bleibendes Popup mit dem Google-Maps-Link. Die
      // quelleId ist stabil; die Position dazu wird beim Klick frisch geholt.
      const quelleId = pos.quelleId;
      el.addEventListener("click", (ev) => {
        ev.stopPropagation();
        popup.current?.remove();
        const aktuell = positionenRef.current.find((p) => p.quelleId === quelleId);
        if (!aktuell) return;
        if (!klickPopup.current) {
          klickPopup.current = new Popup({ closeButton: true, closeOnClick: true, offset: 20, maxWidth: "260px" });
        }
        klickPopup.current.setLngLat([aktuell.lon, aktuell.lat]).setHTML(popupHtml(aktuell, true)).addTo(k);
      });

      marker = new Marker({ element: el }).setLngLat([pos.lon, pos.lat]).addTo(k);
      markerMap.current.set(pos.quelleId, marker);
    }

    if (grenzen.length > 0 && !aktiv) {
      const bounds = grenzen.reduce<[[number, number], [number, number]]>(
        (b, [lon, lat]) => [
          [Math.min(b[0][0], lon), Math.min(b[0][1], lat)],
          [Math.max(b[1][0], lon), Math.max(b[1][1], lat)],
        ],
        [[180, 90], [-180, -90]],
      );
      k.fitBounds(bounds as LngLatBoundsLike, { padding: 80, maxZoom: 15, duration: 400 });
    }
  }, [positionen, bereit, aktiv]);

  // Um grob geortete Geräte einen Unschärfekreis legen: ein 2,4-km-Kreis um
  // Lennox macht sofort klar, dass er nicht genau dort war, sondern irgendwo
  // darin. Präzise Fixes bleiben ohne Kreis, sonst wäre die Karte überladen.
  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    const flaechen: GeoJSON.Feature[] = [];
    for (const pos of positionen) {
      const ort = ortungAus(pos.meta);
      if (!ort?.grob || pos.genauigkeit == null || pos.genauigkeit < 100) continue;
      flaechen.push({
        type: "Feature",
        properties: { farbe: pos.farbe },
        geometry: { type: "Polygon", coordinates: [kreisRing(pos.lon, pos.lat, pos.genauigkeit)] },
      });
    }
    const daten = { type: "FeatureCollection" as const, features: flaechen };

    if (!k.getSource("unschaerfe")) {
      k.addSource("unschaerfe", { type: "geojson", data: daten });
      // Unter die Marker legen, damit die Icons obenauf bleiben.
      const ersteMarkerId = undefined;
      k.addLayer({
        id: "unschaerfe-fill", type: "fill", source: "unschaerfe",
        paint: { "fill-color": ["get", "farbe"], "fill-opacity": 0.15 },
      }, ersteMarkerId);
      k.addLayer({
        id: "unschaerfe-rand", type: "line", source: "unschaerfe",
        paint: { "line-color": ["get", "farbe"], "line-width": 1.5, "line-opacity": 0.7, "line-dasharray": [3, 2] },
      });
    } else {
      (k.getSource("unschaerfe") as GeoJSONSource).setData(daten);
    }
  }, [positionen, bereit]);

  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit || !aktiv) return;

    const pos = positionen.find((p) => p.quelleId === aktiv);
    if (pos) {
      k.easeTo({ center: [pos.lon, pos.lat], zoom: Math.max(k.getZoom(), 14), duration: 500 });
    }
  }, [aktiv, positionen, bereit]);

  // Regenradar: die Bilder holen, den jüngsten Schritt zeigen und die Reihe als
  // Zeitschleife durchlaufen lassen. Bei jedem Bildwechsel bekommt dieselbe
  // Kachelquelle eine neue Vorlage — nach dem ersten Durchlauf liegen die
  // Kacheln im Cache und die Schleife läuft flüssig.
  const zeitleiste = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    const AN = overlays.has("regen");
    let abgebrochen = false;
    let lauf: ReturnType<typeof setInterval> | null = null;
    let auffrischen: ReturnType<typeof setInterval> | null = null;

    const aufraeumen = () => {
      if (lauf) clearInterval(lauf);
      if (auffrischen) clearInterval(auffrischen);
      if (k.getLayer("regen-schicht")) k.removeLayer("regen-schicht");
      if (k.getSource("regen")) k.removeSource("regen");
      if (zeitleiste.current) zeitleiste.current.style.display = "none";
    };

    if (!AN) {
      aufraeumen();
      return;
    }

    let bilder: RadarBild[] = [];
    let i = 0;

    const zeige = () => {
      const bild = bilder[i];
      if (!bild) return;
      const quelle = k.getSource("regen") as GeoJSONSource | undefined;
      if (!k.getSource("regen")) {
        // maxzoom begrenzen: RainViewer liefert Radar nur bis Zoom 7 und
        // antwortet darüber mit einer immer gleichen „Zoom Level Not
        // Supported"-Kachel (nachgemessen: ab z8 an jedem Ort byte-identisch).
        // Mit der Grenze skaliert MapLibre die z7-Kacheln hoch, statt zu feine
        // anzufordern — für großflächigen Niederschlag reicht das.
        k.addSource("regen", { type: "raster", tiles: [bild.kachelVorlage], tileSize: 256, maxzoom: 7 });
        // Der Regen liegt über der Karte, aber halbtransparent, damit Straßen
        // durchscheinen — und unter allen anderen Overlays: sind Blitze oder
        // Unschärfekreise schon da, wird er darunter eingefügt, sonst deckte
        // die Regenschicht sie zu.
        const darueber = ["blitze-schein", "unschaerfe-fill"].find((id) => k.getLayer(id));
        k.addLayer({ id: "regen-schicht", type: "raster", source: "regen", paint: { "raster-opacity": 0.6 } }, darueber);
      } else {
        (quelle as unknown as { setTiles: (t: string[]) => void }).setTiles([bild.kachelVorlage]);
      }
      if (zeitleiste.current) {
        const d = new Date(bild.zeit * 1000);
        zeitleiste.current.textContent =
          (bild.vorhersage ? "⤳ " : "") + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
        zeitleiste.current.style.display = "block";
      }
    };

    const starten = async () => {
      try {
        bilder = await radarBilderLaden();
      } catch {
        return; // Kein Regenradar verfügbar — still übergehen.
      }
      if (abgebrochen || bilder.length === 0) return;
      i = bilder.findIndex((b) => b.vorhersage);
      if (i < 0) i = bilder.length - 1; // ohne Vorhersage: der jüngste Messwert
      zeige();
      lauf = setInterval(() => {
        i = (i + 1) % bilder.length;
        zeige();
      }, 700);
    };

    void starten();
    // RainViewer erneuert die Bilder etwa alle zehn Minuten.
    auffrischen = setInterval(() => {
      if (lauf) clearInterval(lauf);
      void starten();
    }, 5 * 60_000);

    return () => {
      abgebrochen = true;
      aufraeumen();
    };
  }, [overlays, bereit]);

  // Flugverkehr: Flugzeuge im sichtbaren Ausschnitt, alle 20 s und nach jedem
  // Verschieben der Karte neu geholt (Encom bündelt und drosselt die Abrufe).
  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    const aufraeumen = () => {
      for (const m of flugMarker.current.values()) m.remove();
      flugMarker.current.clear();
    };
    if (!overlays.has("flug")) {
      aufraeumen();
      return;
    }

    let abgebrochen = false;
    const laden = async () => {
      const b = k.getBounds();
      let antwort;
      try {
        antwort = await api.fluege(b.getSouth(), b.getWest(), b.getNorth(), b.getEast());
      } catch {
        return; // Flugdaten gerade nicht verfügbar — bestehende Marker bleiben.
      }
      if (abgebrochen) return;
      flugDatenRef.current = antwort.fluege;
      const gesehen = new Set<string>();
      for (const f of antwort.fluege) {
        gesehen.add(f.id);
        let m = flugMarker.current.get(f.id);
        if (m) {
          m.setLngLat([f.lon, f.lat]);
          m.getElement().innerHTML = flugSvg(f.kurs);
        } else {
          const hex = f.id;
          const el = document.createElement("div");
          el.innerHTML = flugSvg(f.kurs);
          el.style.cursor = "pointer";
          el.addEventListener("mouseenter", () => {
            const akt = flugDatenRef.current.find((x) => x.id === hex);
            if (!akt) return;
            if (!popup.current) popup.current = new Popup({ closeButton: false, closeOnClick: false, offset: 14 });
            popup.current.setLngLat([akt.lon, akt.lat]).setHTML(flugPopup(akt)).addTo(k);
          });
          el.addEventListener("mouseleave", () => popup.current?.remove());
          // Klick öffnet ein bleibendes Popup und lädt Strecke, Fluggesellschaft
          // und Maschine von adsbdb nach.
          el.addEventListener("click", (ev) => {
            ev.stopPropagation();
            popup.current?.remove();
            const akt = flugDatenRef.current.find((x) => x.id === hex);
            if (!akt) return;
            if (!klickPopup.current) {
              klickPopup.current = new Popup({ closeButton: true, closeOnClick: true, offset: 14, maxWidth: "300px" });
            }
            klickPopup.current.setLngLat([akt.lon, akt.lat]).setHTML(flugDetailHtml(akt, null)).addTo(k);
            void api
              .flugDetail(hex, akt.rufzeichen)
              .then((d) => {
                // Nur aktualisieren, wenn dieses Popup noch zu diesem Flug gehört.
                if (klickPopup.current?.isOpen()) klickPopup.current.setHTML(flugDetailHtml(akt, d));
              })
              .catch(() => {
                if (klickPopup.current?.isOpen()) klickPopup.current.setHTML(flugDetailHtml(akt, null, true));
              });
          });
          m = new Marker({ element: el }).setLngLat([f.lon, f.lat]).addTo(k);
          flugMarker.current.set(f.id, m);
        }
      }
      for (const [id, m] of flugMarker.current) {
        if (!gesehen.has(id)) {
          m.remove();
          flugMarker.current.delete(id);
        }
      }
    };

    let entprellen: ReturnType<typeof setTimeout> | null = null;
    const beiBewegung = () => {
      if (entprellen) clearTimeout(entprellen);
      entprellen = setTimeout(() => void laden(), 500);
    };

    void laden();
    const takt = setInterval(() => void laden(), 20_000);
    k.on("moveend", beiBewegung);

    return () => {
      abgebrochen = true;
      clearInterval(takt);
      if (entprellen) clearTimeout(entprellen);
      k.off("moveend", beiBewegung);
      aufraeumen();
    };
  }, [overlays, bereit]);

  // Temperatur: an jeder Geräteposition der aktuelle Wert (DWD, alle 5 min).
  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    const aufraeumen = () => {
      for (const m of tempMarker.current.values()) m.remove();
      tempMarker.current.clear();
    };
    if (!overlays.has("temp")) {
      aufraeumen();
      return;
    }

    let abgebrochen = false;
    const laden = async () => {
      const orte = positionenRef.current;
      const gesehen = new Set<string>();
      for (const pos of orte) {
        gesehen.add(pos.quelleId);
        let wetter;
        try {
          wetter = await api.wetter(pos.lat, pos.lon);
        } catch {
          continue;
        }
        if (abgebrochen || wetter.temperatur == null) continue;
        const html = tempFahne(wetter.temperatur);
        let m = tempMarker.current.get(pos.quelleId);
        if (m) {
          m.setLngLat([pos.lon, pos.lat]);
          m.getElement().innerHTML = html;
        } else {
          const el = document.createElement("div");
          el.innerHTML = html;
          el.style.pointerEvents = "none";
          m = new Marker({ element: el, anchor: "bottom", offset: [16, -14] }).setLngLat([pos.lon, pos.lat]).addTo(k);
          tempMarker.current.set(pos.quelleId, m);
        }
      }
      for (const [id, m] of tempMarker.current) {
        if (!gesehen.has(id)) {
          m.remove();
          tempMarker.current.delete(id);
        }
      }
    };

    void laden();
    const takt = setInterval(() => void laden(), 5 * 60_000);
    return () => {
      abgebrochen = true;
      clearInterval(takt);
      aufraeumen();
    };
  }, [overlays, bereit, positionen]);

  // Blitze: Einschläge aus Home Assistant als Punkte, deren Helligkeit mit dem
  // Alter verblasst. Frische Blitze (unter einer Minute) leuchten gelb, ältere
  // werden blass und verschwinden nach 30 Minuten. Alle 15 s nachgeladen, dazu
  // ein eigener Takt, der nur das Verblassen neu zeichnet.
  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    const quelle = () => k.getSource("blitze") as GeoJSONSource | undefined;
    const aufraeumen = () => {
      if (k.getLayer("blitze-schein")) k.removeLayer("blitze-schein");
      if (k.getLayer("blitze-kern")) k.removeLayer("blitze-kern");
      if (k.getSource("blitze")) k.removeSource("blitze");
    };
    if (!overlays.has("blitze")) {
      aufraeumen();
      return;
    }

    let abgebrochen = false;
    let letzte: { lon: number; lat: number; zeitMs: number }[] = [];

    const MAX_ALTER = 30 * 60_000;
    const zeichnen = () => {
      const jetzt = Date.now();
      const features: GeoJSON.Feature[] = [];
      for (const b of letzte) {
        const alter = jetzt - b.zeitMs;
        if (alter > MAX_ALTER) continue;
        // 1 bei frisch, gegen 0 mit zunehmendem Alter.
        const staerke = Math.max(0, 1 - alter / MAX_ALTER);
        features.push({
          type: "Feature",
          properties: { staerke },
          geometry: { type: "Point", coordinates: [b.lon, b.lat] },
        });
      }
      const daten = { type: "FeatureCollection" as const, features };
      if (!k.getSource("blitze")) {
        k.addSource("blitze", { type: "geojson", data: daten });
        // Magenta-violetter Schein — hebt sich vom grün-gelb-roten Regenradar
        // ab, wo das frühere Gelb unterging.
        k.addLayer({
          id: "blitze-schein", type: "circle", source: "blitze",
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["get", "staerke"], 0, 6, 1, 18],
            "circle-color": "#d946ef",
            "circle-opacity": ["*", ["get", "staerke"], 0.5],
            "circle-blur": 0.8,
          },
        });
        // Weißer Kern mit kräftigem violettem Rand: sichtbar auf hellem wie
        // dunklem Untergrund, auch durch die halbtransparente Regenschicht.
        k.addLayer({
          id: "blitze-kern", type: "circle", source: "blitze",
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["get", "staerke"], 0, 2, 1, 4],
            "circle-color": "#ffffff",
            "circle-opacity": ["max", 0.5, ["get", "staerke"]],
            "circle-stroke-width": 2,
            "circle-stroke-color": "#a21caf",
            "circle-stroke-opacity": ["max", 0.5, ["get", "staerke"]],
          },
        });
      } else {
        quelle()?.setData(daten);
      }
    };

    const laden = async () => {
      let antwort;
      try {
        antwort = await api.blitze();
      } catch {
        return;
      }
      if (abgebrochen) return;
      letzte = antwort.blitze.map((b) => ({ lon: b.lon, lat: b.lat, zeitMs: b.zeitMs }));
      zeichnen();
    };

    void laden();
    const holen = setInterval(() => void laden(), 15_000);
    // Das Verblassen läuft weiter, auch ohne neue Daten.
    const verblassen = setInterval(zeichnen, 5_000);

    return () => {
      abgebrochen = true;
      clearInterval(holen);
      clearInterval(verblassen);
      aufraeumen();
    };
  }, [overlays, bereit]);

  // Erdbeben: weltweit als Kreise, Radius und Farbe nach Stärke. Hover zeigt
  // Stärke und Ort; alle fünf Minuten neu (USGS aktualisiert selten öfter).
  const bebenRef = useRef<Erdbeben[]>([]);
  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    const aufraeumen = () => {
      if (k.getLayer("beben-kreis")) k.removeLayer("beben-kreis");
      if (k.getSource("erdbeben")) k.removeSource("erdbeben");
    };
    if (!overlays.has("erdbeben")) {
      aufraeumen();
      return;
    }

    let abgebrochen = false;
    const laden = async () => {
      let antwort;
      try {
        antwort = await api.erdbeben();
      } catch {
        return;
      }
      if (abgebrochen) return;
      bebenRef.current = antwort.beben;
      const features: GeoJSON.Feature[] = antwort.beben.map((b) => ({
        type: "Feature",
        properties: { mag: b.magnitude ?? 0, ort: b.ort ?? "", id: b.id },
        geometry: { type: "Point", coordinates: [b.lon, b.lat] },
      }));
      const daten = { type: "FeatureCollection" as const, features };
      if (!k.getSource("erdbeben")) {
        k.addSource("erdbeben", { type: "geojson", data: daten });
        k.addLayer({
          id: "beben-kreis", type: "circle", source: "erdbeben",
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["get", "mag"], 2, 4, 6, 14, 8, 24],
            "circle-color": ["interpolate", ["linear"], ["get", "mag"], 2, "#facc15", 4, "#f97316", 6, "#dc2626", 8, "#7f1d1d"],
            "circle-opacity": 0.5,
            "circle-stroke-width": 1,
            "circle-stroke-color": "#ffffff",
            "circle-stroke-opacity": 0.7,
          },
        });
        k.on("mousemove", "beben-kreis", (e) => {
          if (!e.features?.length) return;
          k.getCanvas().style.cursor = "pointer";
          const f = e.features[0]!;
          const koords = (f.geometry as GeoJSON.Point).coordinates.slice() as [number, number];
          const mag = f.properties?.mag;
          const ort = f.properties?.ort ?? "";
          if (!popup.current) popup.current = new Popup({ closeButton: false, closeOnClick: false, offset: 8 });
          popup.current
            .setLngLat(koords)
            .setHTML(`<div style="font:12px/1.4 system-ui;color:#1e293b"><b>M ${Number(mag).toFixed(1)}</b><br>${ort}</div>`)
            .addTo(k);
        });
        k.on("mouseleave", "beben-kreis", () => {
          k.getCanvas().style.cursor = "";
          popup.current?.remove();
        });
      } else {
        (k.getSource("erdbeben") as GeoJSONSource).setData(daten);
      }
    };

    void laden();
    const takt = setInterval(() => void laden(), 5 * 60_000);
    return () => {
      abgebrochen = true;
      clearInterval(takt);
      aufraeumen();
    };
  }, [overlays, bereit]);

  // Wind: an jeder Geräteposition ein Pfeil (zeigt, wohin der Wind weht) mit der
  // Geschwindigkeit; alle zehn Minuten neu.
  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    const aufraeumen = () => {
      for (const m of windMarker.current.values()) m.remove();
      windMarker.current.clear();
    };
    if (!overlays.has("wind")) {
      aufraeumen();
      return;
    }

    let abgebrochen = false;
    const laden = async () => {
      const orte = positionenRef.current;
      const gesehen = new Set<string>();
      for (const pos of orte) {
        gesehen.add(pos.quelleId);
        let wind;
        try {
          wind = await api.wind(pos.lat, pos.lon);
        } catch {
          continue;
        }
        if (abgebrochen || wind.geschwindigkeit == null) continue;
        const html = windFahne(wind.geschwindigkeit, wind.richtung);
        let m = windMarker.current.get(pos.quelleId);
        if (m) {
          m.setLngLat([pos.lon, pos.lat]);
          m.getElement().innerHTML = html;
        } else {
          const el = document.createElement("div");
          el.innerHTML = html;
          el.style.pointerEvents = "none";
          m = new Marker({ element: el, anchor: "bottom", offset: [-16, -14] }).setLngLat([pos.lon, pos.lat]).addTo(k);
          windMarker.current.set(pos.quelleId, m);
        }
      }
      for (const [id, m] of windMarker.current) {
        if (!gesehen.has(id)) {
          m.remove();
          windMarker.current.delete(id);
        }
      }
    };

    void laden();
    const takt = setInterval(() => void laden(), 10 * 60_000);
    return () => {
      abgebrochen = true;
      clearInterval(takt);
      aufraeumen();
    };
  }, [overlays, bereit, positionen]);

  // ISS: ein Marker an der aktuellen Position der Raumstation, alle fünf
  // Sekunden nachgeführt — sie umrundet die Erde in rund 90 Minuten.
  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    const aufraeumen = () => {
      issMarker.current?.remove();
      issMarker.current = null;
    };
    if (!overlays.has("iss")) {
      aufraeumen();
      return;
    }

    let abgebrochen = false;
    const laden = async () => {
      let iss;
      try {
        iss = await api.iss();
      } catch {
        return;
      }
      if (abgebrochen) return;
      const html = issSvg();
      if (issMarker.current) {
        issMarker.current.setLngLat([iss.lon, iss.lat]);
      } else {
        const el = document.createElement("div");
        el.innerHTML = html;
        el.style.cursor = "pointer";
        el.addEventListener("mouseenter", () => {
          if (!popup.current) popup.current = new Popup({ closeButton: false, closeOnClick: false, offset: 16 });
          const z = [`<b>ISS</b>`];
          if (iss.hoehe != null) z.push(`Höhe: ${Math.round(iss.hoehe)} km`);
          if (iss.geschwindigkeit != null) z.push(`Tempo: ${Math.round(iss.geschwindigkeit).toLocaleString("de-DE")} km/h`);
          popup.current.setLngLat([iss.lon, iss.lat]).setHTML(`<div style="font:12px/1.5 system-ui;color:#1e293b">${z.join("<br>")}</div>`).addTo(k);
        });
        el.addEventListener("mouseleave", () => popup.current?.remove());
        issMarker.current = new Marker({ element: el }).setLngLat([iss.lon, iss.lat]).addTo(k);
      }
    };

    void laden();
    const takt = setInterval(() => void laden(), 5_000);
    return () => {
      abgebrochen = true;
      clearInterval(takt);
      aufraeumen();
    };
  }, [overlays, bereit]);

  // Ladestationen im sichtbaren Ausschnitt (OpenStreetMap). Klick lädt die
  // Preise von Chargeprice nach. Bei zu weitem Zoom nicht abfragen — Overpass
  // über große Flächen ist teuer.
  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    const aufraeumen = () => {
      for (const m of ladeMarker.current.values()) m.remove();
      ladeMarker.current.clear();
    };
    if (!overlays.has("laden")) {
      aufraeumen();
      return;
    }

    let abgebrochen = false;
    const laden = async () => {
      const b = k.getBounds();
      if (b.getNorth() - b.getSouth() > 2 || b.getEast() - b.getWest() > 3) {
        aufraeumen();
        return; // zu weit herausgezoomt
      }
      let antwort;
      try {
        antwort = await api.ladestationen(b.getSouth(), b.getWest(), b.getNorth(), b.getEast());
      } catch {
        return;
      }
      if (abgebrochen) return;
      ladeDatenRef.current = antwort.stationen;
      const gesehen = new Set<string>();
      for (const st of antwort.stationen) {
        gesehen.add(st.id);
        if (ladeMarker.current.has(st.id)) continue;
        const id = st.id;
        const el = document.createElement("div");
        el.innerHTML = ladeSvg();
        el.style.cursor = "pointer";
        el.addEventListener("mouseenter", () => {
          const s = ladeDatenRef.current.find((x) => x.id === id);
          if (!s) return;
          if (!popup.current) popup.current = new Popup({ closeButton: false, closeOnClick: false, offset: 12 });
          const z = [`<b>${s.name}</b>`];
          if (s.betreiber && s.betreiber !== s.name) z.push(s.betreiber);
          if (s.kw) z.push(`${s.kw} kW`);
          if (s.ladepunkte) z.push(`${s.ladepunkte} Ladepunkte`);
          z.push('<span style="color:#64748b">klicken für Preise</span>');
          popup.current.setLngLat([s.lon, s.lat]).setHTML(`<div style="font:12px/1.5 system-ui;color:#1e293b">${z.join("<br>")}</div>`).addTo(k);
        });
        el.addEventListener("mouseleave", () => popup.current?.remove());
        el.addEventListener("click", (ev) => {
          ev.stopPropagation();
          popup.current?.remove();
          const s = ladeDatenRef.current.find((x) => x.id === id);
          if (!s) return;
          if (!klickPopup.current) {
            klickPopup.current = new Popup({ closeButton: true, closeOnClick: true, offset: 12, maxWidth: "300px" });
          }
          klickPopup.current.setLngLat([s.lon, s.lat]).setHTML(ladeDetailHtml(s, null)).addTo(k);
          void api
            .ladepreise(s.lat, s.lon)
            .then((p) => {
              if (klickPopup.current?.isOpen()) klickPopup.current.setHTML(ladeDetailHtml(s, p));
            })
            .catch(() => {
              if (klickPopup.current?.isOpen()) klickPopup.current.setHTML(ladeDetailHtml(s, null, true));
            });
        });
        const m = new Marker({ element: el }).setLngLat([st.lon, st.lat]).addTo(k);
        ladeMarker.current.set(st.id, m);
      }
      for (const [id, m] of ladeMarker.current) {
        if (!gesehen.has(id)) {
          m.remove();
          ladeMarker.current.delete(id);
        }
      }
    };

    let entprellen: ReturnType<typeof setTimeout> | null = null;
    const beiBewegung = () => {
      if (entprellen) clearTimeout(entprellen);
      entprellen = setTimeout(() => void laden(), 700);
    };
    void laden();
    k.on("moveend", beiBewegung);
    return () => {
      abgebrochen = true;
      if (entprellen) clearTimeout(entprellen);
      k.off("moveend", beiBewegung);
      aufraeumen();
    };
  }, [overlays, bereit]);

  // Schiffe (AIS): Positionen im Ausschnitt, alle 15 s und nach dem Verschieben.
  // Encom abonniert einen neu betrachteten Ausschnitt erst auf Anfrage; der
  // Strom braucht danach ein paar Sekunden, bis Schiffe hereinkommen. Deshalb
  // nach einer Kartenbewegung ein zweites Mal kurz darauf nachladen, damit
  // frisch abonnierte Gegenden nicht erst beim nächsten Takt erscheinen.
  // Marker als Schiffssymbol, in Fahrtrichtung gedreht; Hover zeigt Name/Tempo.
  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    const aufraeumen = () => {
      for (const m of schiffMarker.current.values()) m.remove();
      schiffMarker.current.clear();
    };
    if (!overlays.has("schiffe")) {
      aufraeumen();
      return;
    }

    let abgebrochen = false;
    const laden = async () => {
      const b = k.getBounds();
      let antwort;
      try {
        antwort = await api.schiffe(b.getSouth(), b.getWest(), b.getNorth(), b.getEast());
      } catch {
        return;
      }
      if (abgebrochen) return;
      schiffDatenRef.current = antwort.schiffe;
      const gesehen = new Set<number>();
      for (const s of antwort.schiffe) {
        gesehen.add(s.mmsi);
        let m = schiffMarker.current.get(s.mmsi);
        if (m) {
          m.setLngLat([s.lon, s.lat]);
          m.getElement().innerHTML = schiffSvg(s.kurs);
        } else {
          const mmsi = s.mmsi;
          const el = document.createElement("div");
          el.innerHTML = schiffSvg(s.kurs);
          el.style.cursor = "pointer";
          el.addEventListener("mouseenter", () => {
            const akt = schiffDatenRef.current.find((x) => x.mmsi === mmsi);
            if (!akt) return;
            if (!popup.current) popup.current = new Popup({ closeButton: false, closeOnClick: false, offset: 12 });
            const z = [`<b>${akt.name ?? "Schiff " + akt.mmsi}</b>`];
            if (akt.tempo != null) z.push(`${akt.tempo.toLocaleString("de-DE")} kn`);
            popup.current.setLngLat([akt.lon, akt.lat]).setHTML(`<div style="font:12px/1.4 system-ui;color:#1e293b">${z.join("<br>")}</div>`).addTo(k);
          });
          el.addEventListener("mouseleave", () => popup.current?.remove());
          m = new Marker({ element: el }).setLngLat([s.lon, s.lat]).addTo(k);
          schiffMarker.current.set(s.mmsi, m);
        }
      }
      for (const [mmsi, m] of schiffMarker.current) {
        if (!gesehen.has(mmsi)) {
          m.remove();
          schiffMarker.current.delete(mmsi);
        }
      }
    };

    let entprellenS: ReturnType<typeof setTimeout> | null = null;
    let nachzueglerS: ReturnType<typeof setTimeout> | null = null;
    const beiBewegungS = () => {
      if (entprellenS) clearTimeout(entprellenS);
      entprellenS = setTimeout(() => {
        void laden();
        // Anlaufzeit des frisch abonnierten Ausschnitts überbrücken.
        if (nachzueglerS) clearTimeout(nachzueglerS);
        nachzueglerS = setTimeout(() => void laden(), 3_500);
      }, 600);
    };
    void laden();
    const takt = setInterval(() => void laden(), 15_000);
    k.on("moveend", beiBewegungS);
    return () => {
      abgebrochen = true;
      clearInterval(takt);
      if (entprellenS) clearTimeout(entprellenS);
      if (nachzueglerS) clearTimeout(nachzueglerS);
      k.off("moveend", beiBewegungS);
      aufraeumen();
    };
  }, [overlays, bereit]);

  // Waldbrände (NASA FIRMS): aktive Feuer im Ausschnitt als glühende Punkte,
  // Größe nach Feuerstrahlungsleistung. Hover zeigt Intensität und Alter.
  useEffect(() => {
    const k = karte.current;
    if (!k || !bereit) return;

    const aufraeumen = () => {
      if (k.getLayer("feuer-schein")) k.removeLayer("feuer-schein");
      if (k.getLayer("feuer-kern")) k.removeLayer("feuer-kern");
      if (k.getSource("feuer")) k.removeSource("feuer");
    };
    if (!overlays.has("feuer")) {
      aufraeumen();
      return;
    }

    let abgebrochen = false;
    const laden = async () => {
      const b = k.getBounds();
      if (b.getNorth() - b.getSouth() > 30 || b.getEast() - b.getWest() > 40) {
        aufraeumen();
        return; // FIRMS deckelt die Flächengröße
      }
      let antwort;
      try {
        antwort = await api.feuer(b.getSouth(), b.getWest(), b.getNorth(), b.getEast());
      } catch {
        return;
      }
      if (abgebrochen) return;
      const jetzt = Date.now();
      const features: GeoJSON.Feature[] = antwort.feuer.map((f) => ({
        type: "Feature",
        properties: {
          frp: f.frp ?? 5,
          alterH: Math.round((jetzt - f.zeitMs) / 3_600_000),
          quelle: f.quelle,
        },
        geometry: { type: "Point", coordinates: [f.lon, f.lat] },
      }));
      const daten = { type: "FeatureCollection" as const, features };
      if (!k.getSource("feuer")) {
        k.addSource("feuer", { type: "geojson", data: daten });
        k.addLayer({
          id: "feuer-schein", type: "circle", source: "feuer",
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["get", "frp"], 0, 6, 50, 16, 300, 30],
            "circle-color": "#f97316",
            "circle-opacity": 0.3,
            "circle-blur": 1,
          },
        });
        k.addLayer({
          id: "feuer-kern", type: "circle", source: "feuer",
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["get", "frp"], 0, 2.5, 50, 5, 300, 8],
            "circle-color": ["interpolate", ["linear"], ["get", "frp"], 0, "#fbbf24", 50, "#ef4444", 300, "#b91c1c"],
            "circle-stroke-width": 1,
            "circle-stroke-color": "#fff7ed",
          },
        });
        k.on("mousemove", "feuer-kern", (e) => {
          if (!e.features?.length) return;
          k.getCanvas().style.cursor = "pointer";
          const f = e.features[0]!;
          const koords = (f.geometry as GeoJSON.Point).coordinates.slice() as [number, number];
          const frp = Number(f.properties?.frp);
          const alterH = f.properties?.alterH;
          const z = [`<b>Feuer</b>`, `${frp.toLocaleString("de-DE")} MW`];
          if (alterH != null) z.push(Number(alterH) < 1 ? "vor <1 h" : `vor ${alterH} h`);
          if (f.properties?.quelle) z.push(`<span style="color:#94a3b8">${f.properties.quelle}</span>`);
          if (!popup.current) popup.current = new Popup({ closeButton: false, closeOnClick: false, offset: 8 });
          popup.current.setLngLat(koords).setHTML(`<div style="font:12px/1.4 system-ui;color:#1e293b">${z.join("<br>")}</div>`).addTo(k);
        });
        k.on("mouseleave", "feuer-kern", () => {
          k.getCanvas().style.cursor = "";
          popup.current?.remove();
        });
      } else {
        (k.getSource("feuer") as GeoJSONSource).setData(daten);
      }
    };

    let entprellenF: ReturnType<typeof setTimeout> | null = null;
    const beiBewegungF = () => {
      if (entprellenF) clearTimeout(entprellenF);
      entprellenF = setTimeout(() => void laden(), 700);
    };
    void laden();
    const takt = setInterval(() => void laden(), 10 * 60_000);
    k.on("moveend", beiBewegungF);
    return () => {
      abgebrochen = true;
      clearInterval(takt);
      if (entprellenF) clearTimeout(entprellenF);
      k.off("moveend", beiBewegungF);
      aufraeumen();
    };
  }, [overlays, bereit]);

  return (
    <div className="relative h-full w-full">
      <div ref={behaelter} className="h-full w-full" />
      <OverlayAuswahl gewaehlt={overlays} beiWechsel={setOverlays} />
      <div
        ref={zeitleiste}
        className="pointer-events-none absolute bottom-2 right-2 z-10 hidden rounded bg-surface/90 px-2 py-0.5 font-mono text-xs text-text shadow"
      />
    </div>
  );
}

function geraeteSvg(art: string, farbe: string, richtung: number | null): string {
  const rotation = richtung != null ? `transform="rotate(${richtung} 16 16)"` : "";
  if (art === "bmw") {
    return `<svg width="36" height="36" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="15" fill="${farbe}" stroke="white" stroke-width="2"/>
      <g fill="white" ${rotation}>
        <path d="M16 6 L12 24 L16 20 L20 24 Z" opacity="0.9"/>
      </g>
    </svg>`;
  }
  if (art === "bus") {
    return `<svg width="36" height="36" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="15" fill="${farbe}" stroke="white" stroke-width="2"/>
      <g fill="none" stroke="white" stroke-width="1.8" stroke-linejoin="round">
        <rect x="9" y="8" width="14" height="15" rx="2"/>
        <path d="M9 13 h14"/>
        <path d="M12 23 v2 M20 23 v2"/>
      </g>
    </svg>`;
  }
  if (art === "iphone" || art === "findmy") {
    return `<svg width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="14" fill="${farbe}" stroke="white" stroke-width="2"/>
      <rect x="11" y="8" width="10" height="16" rx="2" fill="none" stroke="white" stroke-width="1.5"/>
      <circle cx="16" cy="21" r="1.2" fill="white"/>
    </svg>`;
  }
  if (art === "freund") {
    return `<svg width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="14" fill="${farbe}" stroke="white" stroke-width="2"/>
      <circle cx="16" cy="13" r="4" fill="white"/>
      <path d="M9 25 a7 7 0 0 1 14 0 Z" fill="white"/>
    </svg>`;
  }
  if (art === "airtag") {
    return `<svg width="28" height="28" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="13" fill="${farbe}" stroke="white" stroke-width="2"/>
      <circle cx="16" cy="16" r="6" fill="none" stroke="white" stroke-width="1.5"/>
      <circle cx="16" cy="16" r="2" fill="white"/>
    </svg>`;
  }
  return `<svg width="30" height="30" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="13" fill="${farbe}" stroke="white" stroke-width="2"/>
    <circle cx="16" cy="16" r="4" fill="white"/>
  </svg>`;
}

function popupHtml(pos: PositionEintrag, mitLink = false): string {
  const meta = pos.meta ?? {};
  const zeilen = [`<b>${pos.quelleName}</b>`, zeitpunktLokal(pos.zeitpunkt)];

  const fmtMeta = (key: string, label: string) => {
    const v = meta[key];
    if (v === undefined || v === null) return;
    if (typeof v === "object" && "wert" in (v as Record<string, unknown>)) {
      const obj = v as { wert: number; einheit?: string };
      zeilen.push(`${label}: ${obj.wert.toLocaleString("de-DE")} ${obj.einheit ?? ""}`);
    } else if (typeof v === "number") {
      zeilen.push(`${label}: ${v.toLocaleString("de-DE")}`);
    }
  };

  if (typeof meta.batterie === "number") zeilen.push(`Batterie: ${meta.batterie}%`);
  if (typeof meta.modell === "string" && meta.modell) zeilen.push(`${meta.modell}`);
  fmtMeta("battery_hv_state_of_charge", "HV-Batterie");
  fmtMeta("range_total_range_last_sent", "Reichweite");
  fmtMeta("range_tank_level", "Tank");
  fmtMeta("vehicle_mileage", "Kilometerstand");
  fmtMeta("iphone_battery_level", "Batterie");
  for (const w of weitereMesswerte(meta)) zeilen.push(`${w.etikett}: ${w.text}`);

  if (pos.hoehe != null) zeilen.push(`Höhe: ${Math.round(pos.hoehe)} m`);

  const anz = ortungAnzeige(ortungAus(meta));
  if (anz) {
    const acc = pos.genauigkeit != null && pos.genauigkeit > 0 ? ` (±${lesbareEntfernung(pos.genauigkeit)})` : "";
    zeilen.push(`<span style="color:${anz.farbe}">${anz.etikett}${acc}</span>`);
  }

  if (mitLink) {
    zeilen.push(
      `<a href="${googleMapsUrl(pos.lat, pos.lon)}" target="_blank" rel="noopener" ` +
        `style="display:inline-flex;align-items:center;gap:4px;margin-top:4px;color:#1e40af;text-decoration:none;font-weight:500">` +
        `In Google Maps öffnen ↗</a>`,
    );
  }

  return `<div style="font:12px/1.6 system-ui;color:#1e293b;min-width:140px">${zeilen.join("<br>")}</div>`;
}
